// Smart Sash Admin Panel - Registration Form Component
// Form for user registration with RFID authentication

import { useRFIDAuth } from '@/services/auth';
import { tokenManager } from '@/services/auth/tokenManager';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface RegistrationFormProps {
  onSuccess?: (login: string) => void;
  sessionId: string;
}

/**
 * Registration Form Component
 */
export const RegistrationForm: React.FC<RegistrationFormProps> = ({ onSuccess, sessionId }) => {
  const { authenticate, isLoading, error: authError } = useRFIDAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!login || !password) {
      setError('Username and password are required');
      return;
    }

    try {
      // Authenticate via RFID auth API
      const requiresPasswordChange = await authenticate(sessionId, login, password);

      if (requiresPasswordChange) {
        tokenManager.setPasswordChangeFlow({
          flowType:
            location.pathname === '/device-registration'
              ? 'device-registration'
              : 'rfid-assignment',
          redirectPath: `${location.pathname}${location.search}`,
          login,
          nextStep: location.pathname === '/device-registration' ? 'lab-selection' : 'activating',
        });
        navigate('/change-password', { replace: true });
        return;
      }

      onSuccess?.(login);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Registration failed. Please try again.';
      setError(errorMessage);
      console.error('Registration error:', err);
    }
  };

  const displayError = error || authError;

  return (
    <form onSubmit={handleSubmit}>
      {displayError && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {displayError}
        </div>
      )}

      <div className="mb-3">
        <label htmlFor="login" className="form-label">
          Username
        </label>
        <input
          type="text"
          className="form-control"
          id="login"
          value={login}
          onChange={(e) => setLogin(e.target.value)}
          disabled={isLoading}
          autoComplete="username"
          placeholder="Enter your username"
          required
        />
      </div>

      <div className="mb-3">
        <label htmlFor="password" className="form-label">
          Password
        </label>
        <input
          type="password"
          className="form-control"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isLoading}
          autoComplete="current-password"
          placeholder="Enter your password"
          required
        />
      </div>

      <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
        {isLoading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            Authenticating...
          </>
        ) : (
          <>
            <i className="fas fa-id-card me-2"></i>
            Continue
          </>
        )}
      </button>
    </form>
  );
};

export default RegistrationForm;
